import React, { memo, useEffect, useState } from 'react'
import { Field as FormikField, Form as FormikForm, useFormikContext } from 'formik'
import styles from './StaffingPositionForm.module.scss'
import { Button, Field, Title } from '@/ui'
import { formFieldNames } from './formFieldNames'
import { useTranslation } from 'react-i18next'
import { WrappedCheckbox } from '@features/contracts/registrationEmployerContract/form/fields'
import { api, LANGUAGE_ENUM, useToggle } from '@/shared'
import { getCurrentLanguage } from '@/i18next'
import { message } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { staffingSelectors, staffThunks, } from '@/entity'
import { handbookSelectors } from '@/entity/handbook'
import { formatDecimalInput } from '@/utils'
import { checkEquality } from '@/utils/checkEquality'
import { ValidationSchema } from './ValidationSchema'
import {isFieldRequired} from '@/utils'
import dayjs from 'dayjs'

import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
dayjs.extend(isSameOrAfter);

export const Form = memo(({ actions, onClose, workPlaceRowSelect }) => {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const lang = getCurrentLanguage()
	const { values, setFieldValue, initialValues, errors, setFieldError } = useFormikContext()
	const positions = useSelector(handbookSelectors.positions)
	const positionsByKnz = useSelector(handbookSelectors.positionsByKnz)
	const [isPositionFilled, togglePositionFilled] = useToggle(false)
	const [disabled, setDisabled] = useState(false)
	const [disabledWP, setDisabledWP] = useState(false)
	const [isCombining, setIsCombining] = useState(false)
	const [isWithoutContract, setIsWithoutContract] = useState(false)
	const [dicContracts, setDicContracts] = useState([])
	const [contractList, setContractList] = useState([])
	const [emptyContract, setEmptyContract] = useState(false)
	const [positionCodeError, setPositionCodeError] = useState(false)
	const [establishedPostError, setEstablishedPostError] = useState(false)
	const [inputValues, setInputValues] = useState({
		factSalary: '',
		salarySupplement: '',
	})
	const isEdit = actions.edit

	const { activeCompany, staffExperience, staffLevel, staffUnit, currentStaff } = useSelector((state) => ({
		activeCompany: state.company.company,
		staffExperience: state.staff.staffExperience,
		staffLevel: state.staff.staffLevel,
		staffUnit: state.staff.staffUnit,
		currentStaff: state.staffing.staffing?.data
	}))

	useEffect(() => {
		setFieldValue('experienceCode', initialValues?.experience?.code)
		setFieldValue('unitCode', initialValues?.unit?.code)
		setFieldValue('levelCode', initialValues?.level?.code)

		if (initialValues?.unit?.code) {
			let body = {
				data: initialValues?.unit?.code,
			}
			dispatch(staffThunks.getStaffLevel(body))
		}
	}, [])

	useEffect(() => {
		if (isEdit) {
			getFlData(values[formFieldNames.iin]).then((value) => {
				if (value.fio !== null) setFieldValue(formFieldNames.employeeInitials, value.fio || '')
				else message.error(t('staff.error_no_contract'), 5)
			})
			if(values[formFieldNames.bin]){
				getBinData(values[formFieldNames.bin])
				setIsWithoutContract(true)
			}
		}
	}, [isEdit])

	useEffect(() => {
		if (isEdit && values[formFieldNames.substitutionExternalId]?.length > 0) {
			const value = getSubstitutionForId(values[formFieldNames.substitutionExternalId])
			setFieldValue(formFieldNames.substitutionIin, value.substitutionIin || '')
			setFieldValue(formFieldNames.substitutionName, value.substitutionName || '')
			setFieldValue('substitutionExternalId', value.externalId || '')
		}
	}, [values[formFieldNames.substitutionExternalId]?.length, isEdit])

	useEffect(() => {
		const valueLength = values[formFieldNames.substitutionIin]?.length

		if (isEdit && valueLength === 12) {
			togglePositionFilled(true)
			setFieldValue('substitutionExternalId', values.substitutionExternalId || '')
			setFieldValue('isReplacement', true)
		} else {
			setFieldValue('substitutionExternalId', '')
		}
	}, [values[formFieldNames.substitutionIin]?.length, isEdit])

	useEffect(() => {
		if (values.contracts) {
			const selectedContract = contractList.find((contract) => contract.externalId === values.contracts)
			setFieldValue('contractExternalId', selectedContract?.externalId)
			setFieldValue('establishedPost', selectedContract?.establishedPost)
			setFieldValue('position', selectedContract?.establishedPost.code)
			setFieldValue('positionCode', selectedContract?.positionCode)
			if (selectedContract && !isCombining) {
				const errors = checkEquality(selectedContract, workPlaceRowSelect)
				setPositionCodeError(errors.positionCodeError)
				setEstablishedPostError(errors.establishedPostError)
			} else if (selectedContract && isCombining) {
				setPositionCodeError(false)
				setEstablishedPostError(false)
			}
		}
	}, [values.contracts, isCombining])

	useEffect(() => {
		if (dicContracts.length === 1) {
			setFieldValue('contracts', dicContracts[0].code)
		}
	}, [dicContracts])

	const wplaceEmployeeList = useSelector(staffingSelectors.wplaceEmployeeList)

	const handleCheckboxChange = (value) => {
		togglePositionFilled(value)
		setFieldValue('isReplacement', !isPositionFilled)
		if (!value) {
			setFieldValue('substitutionExternalId', '')
			setFieldValue('substitutionIin', '')
			setFieldValue('substitutionName', '')
		}
	}

	const handleCombinationCheckboxChange = (value) => {
		setIsCombining(value)
		setFieldValue('isCombining', value)
	}

	const handleWithoutContractCheckboxChange = (value) => {
		setIsWithoutContract(value)
	}

	// Получаем данные с помощью запроса
	const getFlData = async (iin) => {
			setDisabled(true)
			try {
				let params = {
					iin: iin,
				}
				const response = await api.staff.getContractData(params)
				// Обрабатываем полученные данные
				const contracts = response.contractDatas
				let dicContracts = []

				if (contracts) {
					setEmptyContract(false)
					setContractList(response.contractDatas)
					dicContracts = contracts.map((contract) => {
						const infoString = t('col_contract.contract_short_information', {
							contractNumber: contract.contractNumber,
							dateFrom: contract.beginDate,
						})

						return {
							code: contract.externalId,
							kz: infoString,
							rus: infoString,
						}
					})
				} else {
					setContractList([])
					setDicContracts([])
					setFieldValue('contracts', null)
					setFieldValue('fio', null)
					setFieldValue('position', null)
					setFieldValue('positionCode', null)
					setFieldValue('establishedPost', null)

					if(!isCombining){
						setEmptyContract(true)
					}
				}

				setDicContracts(dicContracts)
				return response
			} catch (error) {
				throw error
			}
		}

	const handleClick = () => {
		getFlData(values[formFieldNames.iin]).then((value) => {
			setDisabled(false)
			if (value.fio !== null) setFieldValue(formFieldNames.employeeInitials, value.fio || '')
			else message.error(t('staff.error_no_contract'), 5)
		})
	}

	const getBinData = async (bin) => {
		setDisabled(true)
		const response = await api.profile.getEmployerActivityByBIN({ bin: bin })
		if (response.fullNameKz !== null) {
			let name = {
				[LANGUAGE_ENUM.kz]: response.fullNameKz,
				[LANGUAGE_ENUM.rus]: response.fullNameRu,
			}
			setFieldValue(formFieldNames.binOrgName, name[lang] || '')

			setFieldValue('contracts', null)
			setFieldValue('position', null)
			setFieldValue('positionCode', null)
			setFieldValue('establishedPost', null)
		} else {
			message.error(t('no_data_find'), 5)
		}
		setDisabled(false)
	}


	const handleWPClick = () => {
		setDisabledWP(true)
		setTimeout(() => setDisabledWP(false), 10000)
		const value = getSubstitution(values[formFieldNames.substitutionIin])
		if (value.substitutionName !== null) {
			setFieldValue(formFieldNames.substitutionName, value.substitutionName || '')
			setFieldValue('substitutionExternalId', value.externalId || '')
		} else {
			message.error(t('person_not_found'), 5)
		}
	}

	const getSubstitution = (inputIin) => {
		const substitution = wplaceEmployeeList.find((item) => item.iin === inputIin)
		return {
			substitutionName: substitution ? substitution.fullName : null,
			externalId: substitution ? substitution.externalId : null,
		}
	}

	const getSubstitutionForId = (substitutionExternalId) => {
		const substitution = wplaceEmployeeList.find((item) => item.externalId === substitutionExternalId)
		return {
			substitutionName: substitution ? substitution.fullName : null,
			substitutionIin: substitution ? substitution.iin : null,
			externalId: substitution ? substitution.externalId : null,
		}
	}

	const validateByOrderDate = (value) => {
		if(value){
			let checkIsAfter = dayjs(value, 'DD.MM.YYYY').isSameOrAfter(dayjs(currentStaff?.orderDate, 'DD.MM.YYYY'), 'day')
			if (!checkIsAfter) {
				setFieldError(formFieldNames.startContractDate, t('staff.orderDateError'))
				return t('staff.orderDateError')
			} else {
				return  null
			}
		}
	}

	return (
		<FormikForm className={styles.form}>
			<section>
				<div className={styles.item}>
					<div className={'flex align-items-baseline'}>
						<Title>{t('employee_data')}</Title>
						<div className={styles.mr20}></div>
						<WrappedCheckbox onChange={handleCheckboxChange} value={isPositionFilled} label={t('position_filled')} />
						<div className={styles.mr20}></div>
						<WrappedCheckbox
							onChange={handleCombinationCheckboxChange}
							value={isCombining}
							label={t('positions_combination')}
						/>
						{
							workPlaceRowSelect.isWithoutContract &&
							<>
								<div className={styles.mr20}></div>
								<WrappedCheckbox
									onChange={handleWithoutContractCheckboxChange}
									value={isWithoutContract}
									label={t('without_td')}
								/>
							</>
						}

					</div>
					{isPositionFilled ? (
						<div className={styles.fields}>
							<FormikField name={formFieldNames.substitutionIin}>
								{({ field, meta: { touched, error } }) => (
									<Field
										{...field}
										type="number"
										hideNumberArrows
										isRequired
										label={t('staff.substitute_yin')}
										placeholder={t('enter_count_digitals', { count: 12 })}
										error={touched && error}
										maxLength={12}
									/>
								)}
							</FormikField>
							<div className={styles.fieldAndButton}>
								<Button type="button" disabled={disabledWP} onClick={handleWPClick}>
									{t('find')}
								</Button>
								<FormikField name={formFieldNames.substitutionName}>
									{({ field, meta: { touched, error } }) => (
										<Field
											{...field}
											type="text"
											isRequired
											disabled
											label={t('employee_initials')}
											placeholder={t('filled_automate')}
											error={touched && error}
										/>
									)}
								</FormikField>
							</div>
						</div>
					) : null}
					<div className={styles.fields}>
						<FormikField name={formFieldNames.iin}>
							{({ field, meta: { touched, error } }) => (
								<Field
									{...field}
									type="number"
									hideNumberArrows
									isRequired
									label={t('employee_yin')}
									placeholder={t('enter_count_digitals', { count: 12 })}
									error={touched && error}
									maxLength={12}
								/>
							)}
						</FormikField>
						<div className={styles.fieldAndButton}>
							<Button type="button" disabled={disabled} onClick={handleClick}>
								{t('find')}
							</Button>
							<FormikField name={formFieldNames.employeeInitials}>
								{({ field, meta: { touched, error } }) => (
									<Field
										{...field}
										type="text"
										isRequired
										disabled
										label={t('employee_initials')}
										placeholder={t('filled_automate')}
										error={touched && error}
									/>
								)}
							</FormikField>
						</div>
						{emptyContract && !isWithoutContract ? <p className={styles.error}>{t('staff.error_empty_contract')}</p> : null}
					</div>
					{isWithoutContract ?
						<div className={styles.fields}>
							<FormikField name={formFieldNames.bin}>
								{({ field, meta: { touched, error } }) => (
									<Field
										{...field}
										type="number"
										hideNumberArrows
										isRequired
										label={t('bin')}
										placeholder={t('enter_count_digitals', { count: 12 })}
										error={touched && error}
										maxLength={12}
									/>
								)}
							</FormikField>
							<div className={styles.fieldAndButton}>
								<Button type="button" disabled={disabled} onClick={() => getBinData(values[formFieldNames.bin])}>
									{t('find')}
								</Button>
								<FormikField name={formFieldNames.binOrgName}>
									{({ field, meta: { touched, error } }) => (
										<Field
											{...field}
											type="text"
											isRequired
											disabled
											label={t('name')}
											placeholder={t('filled_automate')}
											error={touched && error}
										/>
									)}
								</FormikField>
							</div>
						</div>
						: null
					}
				</div>
			</section>
			<section>
				<div className={styles.item}>
					<div className={`${styles.fields} ${styles.fields_verticalCenter}`}>
						<Title>{t('contracts_tab.information_about_contract')}</Title>
					</div>
					{(isCombining || isWithoutContract) ? null : (
						<>
							<FormikField name="contracts">
								{({ field, meta: { touched, error } }) => (
									<Field
										{...field}
										fieldType="selectSearch"
										label={t('employment_contract_1')}
										placeholder={t('choose_employment_contract')}
										options={dicContracts}
										error={touched && error}
										isRequired
									/>
								)}
							</FormikField>
							<FormikField name="position">
								{({ field, meta: { touched, error } }) => (
									<>
										<Field
											{...field}
											fieldType="selectSearch"
											label={t('contract.info.position')}
											placeholder={t('filled_automate')}
											options={positions}
											error={touched && error}
											isRequired
											disabled
										/>
										{establishedPostError && <p className={styles.error}>{t('staff.error_position')}</p>}
									</>
								)}
							</FormikField>
							<FormikField name="positionCode">
								{({ field, meta: { touched, error } }) => (
									<>
										<Field
											{...field}
											fieldType="selectSearch"
											label={t('contract.info.nkz_position')}
											placeholder={t('filled_automate')}
											options={positionsByKnz}
											error={touched && error}
											isRequired
											disabled
										/>
										{positionCodeError && <p className={styles.error}>{t('staff.error_position_nkz')}</p>}
									</>
								)}
							</FormikField>
						</>
					)}
					<div className={`${styles.fields} ${styles.threeFields}`}>
						<FormikField name={formFieldNames.tariffRate}>
							{({ field, meta: { touched, error } }) => (
								<Field
									{...field}
									type="number"
									hideNumberArrows
									isRequired
									label={t('contracts_tab.tarrif_rate')}
									placeholder={t('staff_modal.number_bets_placeholder')}
									error={touched && error}
									enablecheckfloat
								/>
							)}
						</FormikField>
						<FormikField name={formFieldNames.factSalary}>
							{({ field, meta: { touched, error } }) => (
								<Field
									{...field}
									isRequired
									label={t('staffing_table.sum')}
									placeholder={t('staff.actual_salary_placeholder')}
									error={touched && error}
									type="text"
									hideNumberArrows
									fieldType={'inputNumber'}
									value={inputValues.factSalary || field.value} // Используем значение из состояния если оно существует
									onChange={(event) => {
										const next = formatDecimalInput(event.target.value, 2)
										field.onChange(next)
									}}
									autoComplete="off"
								/>
							)}
						</FormikField>
						<FormikField name={formFieldNames.salarySupplement}>
							{({ field, meta: { touched, error } }) => (
								<Field
									{...field}
									type="text"
									fieldType={'inputNumber'}
									hideNumberArrows
									label={t('staffing_table.supplement_surcharge')}
									placeholder={t('staff_modal.supplement_surcharge_placeholder')}
									error={touched && error}
									value={inputValues.salarySupplement || field.value} // Используем значение из состояния если оно существует
									onChange={(event) => {
										const next = formatDecimalInput(event.target.value, 2)
										field.onChange(next)
									}}
									autoComplete="off"
								/>
							)}
						</FormikField>
					</div>
					<div className={`${styles.fields} ${styles.threeFields}`}>
						<FormikField name={formFieldNames.startContractDate} validate={validateByOrderDate}>
							{({ field, meta: { touched, error } }) => (
								<Field
									{...field}
									fieldType="datePicker"
									label={t('payment.table.appointmentDate')}
									isRequired
									placeholder={t('default_date_value')}
									error={touched && error}
								/>
							)}
						</FormikField>
						<FormikField name={formFieldNames.endContractDate}>
							{({ field, meta: { touched, error } }) => (
								<Field
									{...field}
									fieldType="datePicker"
									label={t('contracts_tab.end_contract_date')}
									placeholder={t('default_date_value')}
									error={touched && error}
								/>
							)}
						</FormikField>
					</div>
					{activeCompany?.msu && (
						<div className={`${styles.fields} ${styles.threeFields}`}>
							<FormikField name="experienceCode">
								{({ field, meta: { touched, error } }) => {
									const isRequired = isFieldRequired(ValidationSchema(workPlaceRowSelect), 'experienceCode', values)
									return (
										<Field
											{...field}
											fieldType="selectSearch"
											label={t('experience')}
											placeholder={t('choose_from_directory')}
											options={staffExperience.data || []}
											error={touched && error}
											isRequired={isRequired}
										/>
									)
								}}
							</FormikField>
							<FormikField name="unitCode">
								{({ field, meta: { touched, error }, form }) => {
									const isRequired = isFieldRequired(ValidationSchema(workPlaceRowSelect), 'unitCode', values)
									return (
										<Field
											{...field}
											fieldType="selectSearch"
											label={t('unit')}
											placeholder={t('choose_from_directory')}
											options={staffUnit.data || []}
											error={touched && error}
											isRequired={isRequired}
											onChange={(value) => {
												field.onChange(value)
												let body = {
													data: value,
												}
												if(value){
													dispatch(staffThunks.getStaffLevel(body))
												}
												setFieldValue('levelCode', null)
											}}
										/>
									)
								}}
							</FormikField>
							<FormikField name="levelCode">
								{({ field, meta: { touched, error }, form }) => {
									const isRequired = isFieldRequired(ValidationSchema(workPlaceRowSelect), 'levelCode', values)
									return (
										<Field
											{...field}
											fieldType="selectSearch"
											label={t('level')}
											placeholder={t('choose_from_directory')}
											options={staffLevel.data || []}
											error={touched && error}
											isRequired={isRequired}
											disabled={!values.unitCode}
										/>
									)
								}}
							</FormikField>
						</div>
					)}
					<FormikField name={formFieldNames.cause}>
						{({ field, meta: { touched, error } }) => (
							<Field
								{...field}
								fieldType={'textarea'}
								label={t('staffing_table.reason_temporary')}
								placeholder={t('staff_modal.reason_temporary_placeholder')}
								error={touched && error ? t(error) : ''}
								showCount
								maxLength={4000}
								rows={3}
							/>
						)}
					</FormikField>
				</div>
			</section>
			<div className={styles.actions}>
				<Button type="bare" onClick={onClose}>
					{t('cancel')}
				</Button>
				<Button
					textColor="green"
					borderColor="green"
					transparent
					border
					type="submit"
					disabled={isWithoutContract ? false : (positionCodeError || establishedPostError)}
				>
					{t('save')}
				</Button>
			</div>
		</FormikForm>
	)
})
